import { useCallback, useEffect, useRef, useState } from "react";
import type { InvestmentProperty, OwnedProperty, SimState } from "./useREInvestingSim";
import { fmt } from "./useREInvestingSim";
import type { AnyRow } from "@/lib/rowTypes";

export type RECategory =
  | "setup" | "purchase" | "property_action" | "event" | "round" | "final";

export interface REDecisionEntry {
  id: string;
  category: RECategory;
  label: string;
  choice: string;
  consequence: string;
  lumiSignal: string;
  walletImpact: number;   // negative = cost, positive = gain
  scoreDelta: number;     // -10..+15 judgment score
  round?: number;
  ts: number;
}

export type REReadinessLabel =
  | "Capital Strategist"
  | "Steady Operator"
  | "Cash-Flow Apprentice"
  | "Reactive Investor"
  | "Overleveraged Risk"
  | "Patient Analyst";

const KEY = (simId: string) => `re-investing:decisions:${simId}`;

function load(simId: string): REDecisionEntry[] {
  try {
    const raw = localStorage.getItem(KEY(simId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(simId: string, log: REDecisionEntry[]) {
  try { localStorage.setItem(KEY(simId), JSON.stringify(log)); } catch { /* non-critical: failure is safe to ignore here */ }
}

/**
 * Client-side decision-log overlay for the Real Estate Investing
 * simulator. Persisted in localStorage keyed by sim.id so it survives
 * reloads without requiring an edge-function change.
 */
export function useREDecisionLog(simId: string | null | undefined) {
  const [log, setLog] = useState<REDecisionEntry[]>([]);
  const [lastDecision, setLastDecision] = useState<REDecisionEntry | null>(null);
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!simId) { setLog([]); setLastDecision(null); return; }
    const stored = load(simId);
    setLog(stored);
    setLastDecision(stored[stored.length - 1] || null);
  }, [simId]);

  const push = useCallback((entry: Omit<REDecisionEntry, "id" | "ts">) => {
    if (!simId) return;
    const e: REDecisionEntry = {
      ...entry,
      id: `${entry.category}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ts: Date.now(),
    };
    setLog((cur) => {
      const next = [...cur, e];
      save(simId, next);
      return next;
    });
    setLastDecision(e);
    lastId.current = e.id;
  }, [simId]);

  const clear = useCallback(() => {
    if (simId) save(simId, []);
    setLog([]);
    setLastDecision(null);
  }, [simId]);

  /* -------- Domain helpers -------- */

  const recordSetup = useCallback((capital: number, riskStyle: string, market: string) => {
    const aggressive = riskStyle === "high" || riskStyle === "aggressive";
    push({
      category: "setup",
      label: "Investor profile",
      choice: `${fmt(capital)} · ${riskStyle} · ${market}`,
      consequence: `Starting cash ${fmt(capital)} in a ${market} market.`,
      lumiSignal: aggressive
        ? "Aggressive risk preference pushes returns up — and amplifies the cost of mistakes."
        : "Conservative risk preference trades upside for resilience when the cycle turns.",
      walletImpact: 0,
      scoreDelta: 0,
      round: 0,
    });
  }, [push]);

  const recordPurchase = useCallback((property: InvestmentProperty, dpPct: number, years: number, sim: SimState | null) => {
    const dpAmount = Math.round(property.purchase_price * dpPct);
    const heavyLeverage = dpPct <= 0.1;
    const balanced = dpPct >= 0.2 && dpPct <= 0.3;
    const highRisk = property.risk_level === "high";
    const longTerm = years >= 30;
    const score = (balanced ? 6 : heavyLeverage ? -5 : 2)
      + (highRisk ? -3 : 2)
      + (longTerm ? -2 : 4);
    push({
      category: "purchase",
      label: "Property acquisition",
      choice: `${property.property_name} · ${Math.round(dpPct * 100)}% down · ${years}-yr`,
      consequence: `${fmt(dpAmount)} down, financing ${fmt(property.purchase_price - dpAmount)} for ${years} years.`,
      lumiSignal: heavyLeverage
        ? "High leverage juices returns when prices rise — and turns small dips into negative equity fast."
        : balanced
          ? "20–30% down on a rental is the textbook balance: real cash flow and a margin against vacancy."
          : "More down means less leverage, lower cash-on-cash returns, but a calmer holding through downturns.",
      walletImpact: -dpAmount,
      scoreDelta: score,
      round: sim?.current_round,
    });
  }, [push]);

  const recordPropertyAction = useCallback((actionType: string, result: AnyRow, sim: SimState | null) => {
    const cost = Number(result?.cost || result?.spent || 0);
    const gained = Number(result?.proceeds || result?.gain || 0);
    const meta: Record<string, { label: string; signal: string; score: number }> = {
      repair:        { label: "Repair",        signal: "Repairs paid on time protect cash flow and tenant retention.", score: 5 },
      renovate:      { label: "Renovation",    signal: "Renovations only pencil out if rent or value gains exceed the cost.", score: 3 },
      raise_rent:    { label: "Raise rent",    signal: "Raise rent gradually — large hikes invite vacancy.", score: 2 },
      lower_rent:    { label: "Lower rent",    signal: "A modest rent cut beats months of vacancy — empty units bleed.", score: 4 },
      hire_manager:  { label: "Hire manager",  signal: "A manager fee buys back your time; only worth it as the portfolio scales.", score: 1 },
      sell:          { label: "Sell property", signal: "Selling locks the gain — and ends the compounding rental income.", score: 0 },
      refinance:     { label: "Refinance",     signal: "Refinancing trades a lower monthly payment for restart of the amortization clock.", score: 2 },
    };
    const m = meta[actionType] || { label: actionType.replace(/_/g, " "), signal: "Decision recorded.", score: 0 };
    push({
      category: "property_action",
      label: m.label,
      choice: result?.property_name || actionType.replace(/_/g, " "),
      consequence: cost
        ? `Out-of-pocket cost: ${fmt(cost)}.`
        : gained
          ? `Proceeds: ${fmt(gained)}.`
          : (result?.summary || "Action applied to the property."),
      lumiSignal: m.signal,
      walletImpact: gained ? gained : -cost,
      scoreDelta: m.score,
      round: sim?.current_round,
    });
  }, [push]);

  const recordEvent = useCallback((event: AnyRow, choiceIndex: number, outcome: AnyRow, sim: SimState | null) => {
    const choice = event?.choices?.[choiceIndex]?.label || `Option ${choiceIndex + 1}`;
    const delta = Number(outcome?.cash_delta ?? outcome?.delta ?? 0);
    const positive = delta >= 0;
    push({
      category: "event",
      label: event?.title || "Market event",
      choice,
      consequence: outcome?.summary
        || (delta ? `Cash impact ${positive ? "+" : "−"}${fmt(Math.abs(delta))}.` : "Event resolved with no immediate cash impact."),
      lumiSignal: positive
        ? "You answered a surprise without raiding reserves — that is what reserves are for."
        : "Surprises are part of ownership math. Reserves convert a crisis into an expense.",
      walletImpact: delta,
      scoreDelta: positive ? 5 : -4,
      round: sim?.current_round,
    });
  }, [push]);

  const recordRound = useCallback((summary: AnyRow, sim: SimState | null) => {
    const netCash = Number(summary?.net_cash_flow || 0);
    if (!netCash && !summary?.note) return; // skip silent rounds
    push({
      category: "round",
      label: `Quarter ${sim?.current_round ?? "?"}`,
      choice: "Held the portfolio",
      consequence: summary?.note
        || `Net cash flow ${netCash >= 0 ? "+" : "−"}${fmt(Math.abs(netCash))} this quarter.`,
      lumiSignal: netCash >= 0
        ? "Positive cash flow each month is the only definition of a rental that actually works."
        : "Negative cash flow eats reserves — the clock starts the moment you cover a mortgage from savings.",
      walletImpact: netCash,
      scoreDelta: netCash >= 0 ? 2 : -3,
      round: sim?.current_round,
    });
  }, [push]);

  /* -------- Recap builder -------- */

  const buildRecap = useCallback((sim: SimState, derived: {
    netWorth: number; totalPortfolioValue: number;
    totalMortgageDebt: number; monthlyCashFlow: number;
  }) => {
    const totalWallet = log.reduce((s, e) => s + e.walletImpact, 0);
    const totalScore = log.reduce((s, e) => s + e.scoreDelta, 0);
    const strongest = [...log].sort((a, b) => b.scoreDelta - a.scoreDelta)[0] || null;
    const weakest = [...log].sort((a, b) => a.scoreDelta - b.scoreDelta)[0] || null;
    const profit = derived.netWorth - sim.starting_capital;

    let readiness: REReadinessLabel = "Cash-Flow Apprentice";
    if (sim.portfolio.length === 0) readiness = "Patient Analyst";
    else if (totalScore >= 20 && profit > 0) readiness = "Capital Strategist";
    else if (totalScore >= 8 && profit >= 0) readiness = "Steady Operator";
    else if (totalScore >= 0) readiness = "Cash-Flow Apprentice";
    else if (derived.totalMortgageDebt > derived.totalPortfolioValue * 0.85) readiness = "Overleveraged Risk";
    else readiness = "Reactive Investor";

    const tradeoff = (() => {
      if (derived.monthlyCashFlow < 0)
        return "You optimized for size over income — a portfolio that loses money each month is a hobby, not an investment.";
      if (derived.totalMortgageDebt > derived.totalPortfolioValue * 0.8)
        return "Heavy leverage made the upside bigger and the floor lower. Small price drops would erase equity fast.";
      if (sim.total_repair_costs > sim.total_rental_income * 0.35)
        return "Repair costs outran rental income — buying older or higher-risk properties without a reserve plan is the trap.";
      return "You balanced acquisition, leverage, and reserves — the three real estate knobs every investor adjusts.";
    })();

    const takeaway = profit > 0
      ? "Real estate rewards patience, leverage discipline, and a real maintenance reserve. Cash flow first, appreciation second."
      : "Most real estate losses come from overpaying, overleveraging, or skipping reserves. Pick the boring deal that pencils.";

    const nextAction = profit > 0
      ? "Open Tax Strategy next: depreciation and pass-through deductions are most of what makes real estate compelling."
      : "Try the Risk Protection module: insurance + emergency reserves are what keeps one bad month from ending a portfolio.";

    return { totalWallet, totalScore, readiness, strongest, weakest, tradeoff, takeaway, nextAction };
  }, [log]);

  return {
    log,
    lastDecision,
    clear,
    recordSetup,
    recordPurchase,
    recordPropertyAction,
    recordEvent,
    recordRound,
    buildRecap,
  };
}